﻿import {
    Camera,
    DataCaptureContext,
    DataCaptureView,
    FrameSourceState,
    configure,
    CameraSwitchControl,
} from 'scandit-web-datacapture-core';
import {
    BarcodeCapture,
    barcodeCaptureLoader,
    BarcodeCaptureSettings,
    Symbology,
} from 'scandit-web-datacapture-barcode';

import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
import { fetchRequest } from '@tk/utilities/tk.fetch';
import render from '@tk/utilities/tk.render';

export default class KWScan extends TKCustomElementFactory {
    view: DataCaptureView;
    context?: DataCaptureContext;
    settings?: BarcodeCaptureSettings;
    searchForm?: HTMLFormElement;
    barcodeStorage?: string[];
    multiSubmitBtn?: HTMLButtonElement;
    artList?: HTMLElement;
    lastScan?: string;
    barcodeCapture?: BarcodeCapture;

    constructor() {
        super();
        this.view = new DataCaptureView();
        const dialog = this.closest('dialog');
        if (!dialog) return;
        this.multiSubmitBtn = dialog.querySelector('[data-tk-scan-multiscan') || undefined;
        this.artList = dialog.querySelector('[data-tk-scan-artlist]') || undefined;

        this.searchForm = document.querySelector('[data-tk-search]') || undefined;
    }

    connectedCallback(): void {
        this.loader();
        this.initScan();
    }

    loader() {
        this.view.connectToElement(this);
        this.view.showProgressBar();
        this.view.setProgressBarMessage('Loading ...');
    }

    hideLoader() {
        this.view.hideProgressBar();
    }

    setScanningBehaviour() {
        this.settings = new BarcodeCaptureSettings();
        this.settings.enableSymbologies([
            Symbology.Code128,
            Symbology.Code39,
            Symbology.QR,
            Symbology.EAN8,
            Symbology.UPCE,
            Symbology.EAN13UPCA,
        ]);
    }

    async initScan() {
        // Set the progress bar to be in an indeterminate state
        this.view.setProgressBarPercentage(null);
        this.view.setProgressBarMessage('Accessing Camera...');

        await configure({
            licenseKey: process.env.SCANDIT_API || '',
            libraryLocation: new URL(
                'webportal/config/layout/themes/tk/vendor/scandit/library/engine',
                document.baseURI.split('/').slice(0, 4).join('/'), // for pages like search
            ).toString(),
            moduleLoaders: [barcodeCaptureLoader()],
        });

        this.context = await DataCaptureContext.create();
        await this.view.setContext(this.context);

        // set scanning behaviour
        this.setScanningBehaviour();

        if (!this.context && !this.settings) return;
        this.barcodeCapture = await BarcodeCapture.forContext(this.context, this.settings!);
        this.listener(this.barcodeCapture);

        const camera: Camera = Camera.default;
        const cameraSettings = BarcodeCapture.recommendedCameraSettings;

        if (camera) {
            await camera.applySettings(cameraSettings);
            await this.context.setFrameSource(camera);
            await camera.switchToDesiredState(FrameSourceState.On);
        }

        this.view.addControl(new CameraSwitchControl());

        if(this.multiSubmitBtn) {
            this.barcodeStorage = [];

            const action = this.submitBarcodes.bind(this);
            this.pushListener({event: 'click', element: this.multiSubmitBtn, action});
        }

        this.hideLoader();
    }

    listener(barcodeCapture: BarcodeCapture) {
        const listener = {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            didScan: async (barcodeCapture: BarcodeCapture, session: any) => {
                const barcode = session.newlyRecognizedBarcodes[0];

                barcodeCapture.setEnabled(false);
                setTimeout(() => barcodeCapture.setEnabled(true), 700)

                if (barcode.data === this.lastScan) return;

                if (this.multiSubmitBtn) {
                    this.lastScan = barcode.data;
                    this.storeBarcode(barcode.data);
                } else {
                    this.searchArticle(barcode.data);
                    barcodeCapture.setEnabled(false);
                }
            },
        };

        barcodeCapture.addListener(listener);
    }

    searchArticle(barCode:string):void {
        if (!this.searchForm) return;
        const input = this.searchForm.querySelector('input');

        if (!input) return;

        input.value = barCode;
        this.searchForm.submit();
    }

    storeBarcode(barCode:string):void {
        this.barcodeStorage?.push(barCode);

        const contentURL = this.multiSubmitBtn!.getAttribute('data-tk-content-url');

        if (!contentURL) throw new Error('KWScan: no content URL');

        const barcodeParts = barCode.split(',');
        const artInternalNo = barcodeParts[barcodeParts.length -1].split('_')[1];

        fetchRequest({
            requestURL: contentURL,
            resolveHandler: this.insertArticles.bind(this),
            payload: { artInternalNo },
        });
    }

    insertArticles(response:TKResponse) {
        if (!response.dataAsHtml.trim()) {
            this.barcodeStorage?.pop()
            return;
        }

        const body = render(response.dataAsHtml, true);
        this.artList?.insertAdjacentHTML('beforeend', body.innerHTML);

        this.multiSubmitBtn!.disabled = false;
    }

    submitBarcodes() {
        const storageURL = this.multiSubmitBtn?.getAttribute('data-tk-storage-url');

        if (this.barcodeStorage!.length === 0 || !storageURL) throw new Error('KWScan: cannot be stored');

        this.barcodeCapture!.setEnabled(false);

        const textContent = this.barcodeStorage!.join('\n');

        fetchRequest({
            requestURL: storageURL,
            resolveHandler: () => {},
            payload: { textContent },
        })
    }
}